import { z } from 'zod';
import { EventSchema } from './event';
import { EventCategorySchema } from './event-category';
import { TicketSchema, TicketStatusSchema } from './ticket';
import { BlockSchema } from './block';
import { LoyaltyEventSchema } from './loyalty';

/**
 * Öffentliche Kategorie-Kurzform für Kalender-Filter.
 * Response-Type für `GET /api/public/event-categories`.
 */
export const PublicEventCategorySchema = EventCategorySchema.pick({
  id: true,
  slug: true,
  name: true,
  color: true,
});

/** TypeScript Type — abgeleitet aus PublicEventCategorySchema */
export type PublicEventCategory = z.infer<typeof PublicEventCategorySchema>;

/**
 * Öffentliches Event mit Kategorie-Daten.
 * Response-Type für `GET /api/public/events` (mit category-Include).
 */
export const PublicEventItemSchema = z.object({
  id: z.string(),
  title: z.string(),
  opponent: z.string().nullable(),
  opponentLogoUrl: z.string().nullable().optional(),
  startsAt: z.coerce.date(),
  endsAt: z.coerce.date().nullable(),
  location: z.string().nullable(),
  locationUrl: z.string().nullable().optional(),
  imageUrl: z.string().nullable(),
  maxParticipants: z.number().int().nullable(),
  totalTickets: z.number().int().nullable(),
  status: z.string(),
  categoryId: z.string(),
  category: PublicEventCategorySchema.nullable().optional(),
});

/** TypeScript Type — abgeleitet aus PublicEventItemSchema */
export type PublicEventItem = z.infer<typeof PublicEventItemSchema>;

/**
 * Event-List-Item mit Category-Daten und aggregierten Counts.
 * Response-Type für `GET /api/events`.
 */
export const EventListItemSchema = EventSchema.extend({
  category: EventCategorySchema.pick({
    id: true,
    slug: true,
    name: true,
    color: true,
    hasTicketing: true,
  }),
  _count: z.object({
    registrations: z.number().int(),
    tickets: z.number().int(),
    checkIns: z.number().int(),
    waitlistEntries: z.number().int(),
  }),
});

/** TypeScript Type — abgeleitet aus EventListItemSchema */
export type EventListItem = z.infer<typeof EventListItemSchema>;

/**
 * Ticket mit User-Daten für Tabellen-Views.
 * Response-Type für `GET /api/tickets/event/:eventId`.
 */
export const TicketWithUserSchema = TicketSchema.extend({
  user: z
    .object({
      id: z.string(),
      displayName: z.string().nullable(),
      firstName: z.string().nullable(),
      lastName: z.string().nullable(),
      email: z.string().email(),
    })
    .nullable(),
});

/** TypeScript Type — abgeleitet aus TicketWithUserSchema */
export type TicketWithUser = z.infer<typeof TicketWithUserSchema>;

/**
 * User-Detail mit allen Relations für die Detail-Page.
 * Response-Type für `GET /api/persons/:id/detail`.
 */
export const UserDetailSchema = z.object({
  id: z.string(),
  email: z.string().email(),
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  displayName: z.string().nullable(),
  discordId: z.string().nullable(),
  avatarUrl: z.string().nullable(),
  loyaltyScore: z.number().int(),
  isActive: z.boolean(),
  lastSeenAt: z.coerce.date().nullable(),
  createdAt: z.coerce.date(),
  tickets: z.array(
    z.object({
      id: z.string(),
      status: TicketStatusSchema,
      seatInfo: z.string().nullable(),
      assignedAt: z.coerce.date().nullable(),
      event: z.object({
        id: z.string(),
        title: z.string(),
        startsAt: z.coerce.date(),
      }),
    }),
  ),
  registrations: z.array(
    z.object({
      id: z.string(),
      status: z.string(),
      registeredAt: z.coerce.date(),
      event: z.object({
        id: z.string(),
        title: z.string(),
        startsAt: z.coerce.date(),
      }),
    }),
  ),
  blocks: z.array(BlockSchema),
  _count: z.object({
    tickets: z.number().int(),
    checkIns: z.number().int(),
  }),
});

/** TypeScript Type — abgeleitet aus UserDetailSchema */
export type UserDetail = z.infer<typeof UserDetailSchema>;

/**
 * Loyalty-Ledger eines Users — Zusammenfassung + alle Einträge.
 * Response-Type für `GET /api/loyalty/user/:userId`.
 */
export const LoyaltyLedgerSchema = z.object({
  userId: z.string(),
  totalScore: z.number().int(),
  positiveTotal: z.number().int(),
  negativeTotal: z.number().int(),
  entries: z.array(
    LoyaltyEventSchema.extend({
      event: z.object({ id: z.string(), title: z.string() }).nullable(),
    }),
  ),
});

/** TypeScript Type — abgeleitet aus LoyaltyLedgerSchema */
export type LoyaltyLedger = z.infer<typeof LoyaltyLedgerSchema>;

/**
 * Filter-Schema für Ticket-Listen.
 * Wird als Query-Parameter an `GET /api/tickets/event/:eventId` übergeben.
 */
export const TicketListFiltersSchema = z.object({
  status: z.enum(['free', 'assigned', 'revoked']).optional(),
  search: z.string().optional(),
});

/** TypeScript Type — abgeleitet aus TicketListFiltersSchema */
export type TicketListFilters = z.infer<typeof TicketListFiltersSchema>;

/**
 * Filter-Schema für Event-Listen.
 * Wird als Query-Parameter an `GET /api/events` übergeben.
 */
export const EventListFiltersSchema = z.object({
  status: z.enum(['draft', 'published', 'cancelled', 'completed']).optional(),
  categoryId: z.string().optional(),
  search: z.string().optional(),
  dateFrom: z.string().optional(),
  dateTo: z.string().optional(),
  limit: z.number().int().positive().optional(),
  offset: z.number().int().nonnegative().optional(),
});

/** TypeScript Type — abgeleitet aus EventListFiltersSchema */
export type EventListFiltersType = z.infer<typeof EventListFiltersSchema>;

/**
 * Filter-Schema für User-Listen.
 * Wird als Query-Parameter an `GET /api/persons` übergeben.
 */
export const UserListFiltersSchema = z.object({
  search: z.string().optional(),
  isActive: z.boolean().optional(),
  hasBlock: z.boolean().optional(),
});

/** TypeScript Type — abgeleitet aus UserListFiltersSchema */
export type UserListFilters = z.infer<typeof UserListFiltersSchema>;
